import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { isAxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import {
  FolderOpen,
  FileText,
  Download,
  ExternalLink,
  CheckCircle,
  Clock,
  BookOpen,
  ShieldCheck,
  Scale,
  Loader2,
  Lock,
  X,
  type LucideIcon,
} from 'lucide-react';
import api from '../services/api';
import { KlachtenSectie } from './klachten/KlachtenSectie';
import klachtenreglement from '../data/klachtenreglement.json';

/**
 * Mijn Cockpit → Contracten & Documenten voor de opdrachtgever.
 * Spiegel van de professional-weergave (ProfessionalBelagomPage → ContractenView):
 * 1. organisatiedocumenten (KvK-verificatie en screening),
 * 2. de ondertekende opdrachtgeversovereenkomst (PDF),
 * 3. Overeenkomsten van Opdracht met professionals,
 * 4. Belagom-documenten en 5. het klachtenreglement.
 * Contracten zijn alleen voor de hoofdgebruiker en de team-rol Admin
 * (backend: toegang_cockpit); andere teamleden zien alleen 4 en 5.
 */

const BELAGOM_DOCUMENTS = [
  { id: 'algemene-voorwaarden', titleKey: 'documents.termsTitle', descKey: 'documents.termsDesc', icon: BookOpen, url: '/terms' },
  { id: 'klachtenreglement', titleKey: 'documents.complaintsTitle', descKey: 'documents.complaintsDesc', icon: Scale, url: klachtenreglement.pdf },
  { id: 'privacyverklaring', titleKey: 'documents.privacyTitle', descKey: 'documents.privacyDesc', icon: ShieldCheck, url: '/privacy' },
];

interface ProfessionalContract {
  id: string;
  kind: 'project' | 'pool';
  status: 'WACHT_OP_WERKGEVER' | 'WACHT_OP_PROFESSIONAL' | 'GETEKEND';
  professional_name: string | null;
  subject: string | null;
  created_at: string;
  sealed_at: string | null;
  addenda_count: number;
}

interface EmployerDocuments {
  organisatie: { bedrijfsnaam: string | null; kvk_nummer: string | null; kvk_geverifieerd: boolean } | null;
  screening: { status: string; bedrijfsnaam: string; kvk_nummer: string; btw_nummer: string | null; submitted_at: string } | null;
  signed_contract: {
    id: number;
    signed_at: string;
    contract_version: string;
    contract_hash: string | null;
    methode: 'sms-2fa' | 'handtekening';
  } | null;
  professional_contracts: ProfessionalContract[];
}

const cardClass = 'flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow';
const emptyClass = 'rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500';
const linkClass = 'inline-flex items-center gap-1 mt-2 text-xs font-medium text-[#0B1F3A] hover:underline';

const SectionTitle: React.FC<{ icon: LucideIcon; children: React.ReactNode }> = ({ icon: Icon, children }) => (
  <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
    <Icon size={18} className="text-[#0B1F3A]" />
    {children}
  </h2>
);

const CardIcon: React.FC<{ icon?: LucideIcon }> = ({ icon: Icon = FileText }) => (
  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-blue-50">
    <Icon size={20} className="text-[#0B1F3A]" />
  </div>
);

const ProfessionalContractModal: React.FC<{ contractId: string; onClose: () => void }> = ({ contractId, onClose }) => {
  const { t, i18n } = useTranslation('employer');
  const { data: contract, isLoading } = useQuery({
    queryKey: ['employer-professional-contract', contractId],
    queryFn: async () => {
      const res = await api.get(`/employer/my-documents/professional-contracts/${contractId}`);
      return res.data?.data as {
        status: string;
        contract_html: string;
        contract_sha256: string | null;
        sealed_at: string | null;
        werkgever_getekend_op: string | null;
        professional_getekend_op: string | null;
        addenda: Array<{ id: string; volgnummer: number; addendum_html: string; sealed_at: string }>;
      };
    },
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString(i18n.language) : '—');
  const contractClass = 'prose prose-sm max-w-none [&_table]:w-full [&_td]:border [&_td]:p-1 [&_th]:border [&_th]:p-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('contractenDocumenten.sluiten')}
          className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <X size={18} />
        </button>

        {isLoading || !contract ? (
          <div className="flex items-center justify-center p-12 text-gray-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className={contractClass} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.contract_html) }} />

            {contract.addenda.map((a) => (
              <div key={a.id} className="border-t border-gray-200 pt-6">
                <p className="text-xs text-gray-500 mb-2">
                  {t('contractenDocumenten.addendum', { number: a.volgnummer })} &middot; {fmtDateTime(a.sealed_at)}
                </p>
                <div className={contractClass} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.addendum_html) }} />
              </div>
            ))}

            {contract.status === 'GETEKEND' && (
              <div className="flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-800">
                <ShieldCheck size={20} className="flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('contractenDocumenten.contractVerzegeld')}</p>
                  <p>
                    {t('contractenDocumenten.getekendWerkgever')}: {fmtDateTime(contract.werkgever_getekend_op)} &middot;{' '}
                    {t('contractenDocumenten.getekendProfessional')}: {fmtDateTime(contract.professional_getekend_op)}
                  </p>
                  {contract.contract_sha256 && (
                    <p className="mt-1 break-all font-mono text-xs text-green-700">SHA-256: {contract.contract_sha256}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const EmployerContractenDocumenten: React.FC = () => {
  const { t, i18n } = useTranslation(['employer', 'belagom']);
  const [openContractId, setOpenContractId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['employer-my-documents'],
    queryFn: async () => {
      const res = await api.get('/employer/my-documents');
      return res.data?.data as EmployerDocuments;
    },
    retry: (count, err) => !(isAxiosError(err) && err.response?.status === 403) && count < 2,
  });
  const geenToegang = isAxiosError(error) && error.response?.status === 403;

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }) : null;

  const downloadContract = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/employer/contract/pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Belagom-opdrachtgeversovereenkomst-v${data?.signed_contract?.contract_version ?? ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('contractenDocumenten.downloadFout'));
    } finally {
      setDownloading(false);
    }
  };

  const { organisatie, screening, signed_contract, professional_contracts = [] } = data || ({} as Partial<EmployerDocuments>);
  const screeningLabel = (status: string) =>
    t(`contractenDocumenten.screeningStatus_${status}`, { defaultValue: status });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">{t('contractenDocumenten.titel')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('contractenDocumenten.intro')}</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <p className="text-gray-500">{t('belagom:loading.documents')}</p>
        </div>
      ) : geenToegang ? (
        <div className={`${emptyClass} flex flex-col items-center gap-2`}>
          <Lock size={20} className="text-gray-400" />
          {t('contractenDocumenten.geenToegang')}
        </div>
      ) : error ? (
        <div className={emptyClass}>{t('contractenDocumenten.laadFout')}</div>
      ) : (
        <>
          {/* ---- Section 1: Organisatiedocumenten ---- */}
          <section>
            <SectionTitle icon={FolderOpen}>{t('contractenDocumenten.organisatieDocumenten')}</SectionTitle>
            {!organisatie?.kvk_nummer && !screening ? (
              <div className={emptyClass}>{t('contractenDocumenten.geenOrganisatieDocumenten')}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {organisatie?.kvk_nummer && (
                  <div className={cardClass}>
                    <CardIcon icon={ShieldCheck} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-gray-900 truncate">{t('contractenDocumenten.kvkRegistratie')}</p>
                        {organisatie.kvk_geverifieerd && <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {[organisatie.bedrijfsnaam, `KvK ${organisatie.kvk_nummer}`].filter(Boolean).join(' · ')}
                      </p>
                      <p className={`text-xs mt-0.5 ${organisatie.kvk_geverifieerd ? 'text-gray-500' : 'text-amber-700'}`}>
                        {t(organisatie.kvk_geverifieerd ? 'contractenDocumenten.kvkGeverifieerd' : 'contractenDocumenten.kvkNietGeverifieerd')}
                      </p>
                    </div>
                  </div>
                )}
                {screening && (
                  <div className={cardClass}>
                    <CardIcon />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-gray-900 truncate">{t('contractenDocumenten.screening')}</p>
                        {screening.status === 'approved' && <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {t('contractenDocumenten.ingediendOp')} {fmtDate(screening.submitted_at)}
                        {screening.btw_nummer ? ` · ${t('contractenDocumenten.btw')} ${screening.btw_nummer}` : ''}
                      </p>
                      <p className={`text-xs mt-0.5 ${screening.status === 'approved' ? 'text-gray-500' : 'text-amber-700'}`}>
                        {screeningLabel(screening.status)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ---- Section 2: Getekende opdrachtgeversovereenkomst ---- */}
          <section>
            <SectionTitle icon={FileText}>{t('belagom:section.signedContract')}</SectionTitle>
            {signed_contract ? (
              <div className={cardClass}>
                <CardIcon />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-gray-900 truncate">{t('contractenDocumenten.opdrachtgeversovereenkomst')}</p>
                    <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {`${t('belagom:label.signedOn')} ${fmtDate(signed_contract.signed_at)} · ${t('belagom:label.version')} ${signed_contract.contract_version}`}
                    {signed_contract.methode === 'sms-2fa' && ` · ${t('contractenDocumenten.viaSms')}`}
                  </p>
                  {signed_contract.contract_hash && (
                    <p className="text-[11px] text-gray-400 mt-0.5 font-mono truncate" title={signed_contract.contract_hash}>
                      SHA-256: {signed_contract.contract_hash}
                    </p>
                  )}
                  <button type="button" onClick={downloadContract} disabled={downloading} className={`${linkClass} disabled:opacity-50`}>
                    {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {t('belagom:button.download')}
                  </button>
                </div>
              </div>
            ) : (
              <div className={emptyClass}>
                <Clock size={20} className="mx-auto mb-2 text-gray-400" />
                {t('contractenDocumenten.geenContract')}
              </div>
            )}
          </section>

          {/* ---- Section 3: Overeenkomsten van Opdracht met professionals ---- */}
          <section>
            <SectionTitle icon={FileText}>{t('contractenDocumenten.professionalContracten')}</SectionTitle>
            {professional_contracts.length === 0 ? (
              <div className={emptyClass}>{t('contractenDocumenten.geenProfessionalContracten')}</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {professional_contracts.map((c) => {
                  const signed = c.status === 'GETEKEND';
                  const kind = t(c.kind === 'project' ? 'contractenDocumenten.projectovereenkomst' : 'contractenDocumenten.pooloverenkomst');
                  return (
                    <div key={c.id} className={cardClass}>
                      <CardIcon />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm text-gray-900 truncate">
                            {c.professional_name ?? t('contractenDocumenten.onbekendeProfessional')}
                          </p>
                          {signed && <CheckCircle size={14} className="text-blue-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{c.subject ? `${kind} · ${c.subject}` : kind}</p>
                        <p className={`text-xs mt-0.5 ${signed ? 'text-gray-500' : 'text-amber-700'}`}>
                          {signed
                            ? `${t('belagom:label.signedOn')} ${fmtDate(c.sealed_at)}`
                            : t(c.status === 'WACHT_OP_WERKGEVER' ? 'contractenDocumenten.wachtOpUw' : 'contractenDocumenten.wachtOpProfessional')}
                          {c.addenda_count > 0 && ` · ${t('contractenDocumenten.addendaAantal', { count: c.addenda_count })}`}
                        </p>
                        <button type="button" onClick={() => setOpenContractId(c.id)} className={linkClass}>
                          <ExternalLink size={12} />
                          {t('contractenDocumenten.bekijken')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {openContractId && <ProfessionalContractModal contractId={openContractId} onClose={() => setOpenContractId(null)} />}

      {/* ---- Section 4: Belagom documenten ---- */}
      <section>
        <SectionTitle icon={BookOpen}>{t('belagom:section.belagomDocuments')}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {BELAGOM_DOCUMENTS.map((doc) => (
            <div key={doc.id} className={cardClass}>
              <CardIcon icon={doc.icon} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-gray-900">{t(`belagom:${doc.titleKey}`)}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t(`belagom:${doc.descKey}`)}</p>
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className={linkClass}>
                  <ExternalLink size={12} />
                  {t('belagom:button.open')}
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Section 5: Klachtenreglement & formulier ---- */}
      <KlachtenSectie formulierAchterKnop />

      <p className="text-xs text-gray-400">{t('contractenDocumenten.wijzigingen')}</p>
    </div>
  );
};

export default EmployerContractenDocumenten;
